import { styled } from '@linaria/react';

import { bodyFont, fonts, layout, primitives, semantic, size, space } from '../../theme/tokens';

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-800']};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: ${space['space-1600']} ${space['space-600']} ${space['space-1000']};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space['space-2000']} ${space['space-2400']} ${space['space-1600']};
  }
`;

export const RestaurantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-300']};
`;

const restaurantCard = `
  display: flex;
  align-items: center;
  gap: ${space['space-500']};
  width: 100%;
  min-height: 72px;
  padding: ${space['space-500']};
  border-radius: ${size.radius['radius-lg']};
  background: ${semantic.bg.canvas};
`;

export const RestaurantOption = styled.button<{ $selected?: boolean }>`
  ${restaurantCard}
  border: ${({ $selected }) =>
    $selected
      ? `${size.stroke['stroke-thick']} solid ${semantic.border.selected}`
      : `${size.stroke['stroke-default']} solid ${semantic.border.default}`};
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const SelectedRestaurant = styled.div`
  ${restaurantCard}
  border: ${size.stroke['stroke-default']} solid ${semantic.border.default};
`;

export const SelectedRestaurantGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-300']};
`;

export const ChangeRestaurantButton = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.brand};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const RestaurantOptionText = styled.span`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: ${space['space-100']};
  min-width: 0;
`;

export const RestaurantName = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.primary};
`;

export const RestaurantSubtitle = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.secondary};
`;

export const RestaurantThumb = styled.img`
  width: 80px;
  height: 80px;
  border-radius: ${size.radius['radius-xs']};
  object-fit: cover;
  flex-shrink: 0;
`;

export const RestaurantThumbPlaceholder = styled.span`
  width: 80px;
  height: 80px;
  border-radius: ${size.radius['radius-xs']};
  background: ${semantic.bg.surfaceSubtle};
  flex-shrink: 0;
`;

export const PanelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-600']};
  width: 100%;
`;

export const FormPanel = styled.div`
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border: ${size.stroke['stroke-default']} solid ${semantic.border.default};
  border-radius: ${size.radius['radius-xl']};
  background: ${semantic.bg.canvas};
`;

export const CardSection = styled.div<{ $compact?: boolean; $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $wide }) => ($wide ? space['space-800'] : space['space-600'])};
  padding: ${({ $compact }) =>
    $compact
      ? `${space['space-600']} ${space['space-500']}`
      : `${space['space-1000']} ${space['space-500']}`};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${({ $compact }) =>
      $compact
        ? `${space['space-900']} ${space['space-800']} ${space['space-1200']}`
        : `${space['space-1200']} ${space['space-800']}`};
  }
`;

export const FieldLabel = styled.p`
  margin: 0;
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.primary};
`;

export const TimeslotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${space['space-200']};
  width: 100%;

  @media (min-width: ${layout.desktopBreakpoint}) {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }
`;

export const EmptyTimeslots = styled.p`
  margin: 0;
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.secondary};
`;

export const MenuCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space['space-500']};
  width: 100%;
  box-sizing: border-box;
  padding: ${space['space-800']};
  border-radius: ${size.radius['radius-xl']};
  background: ${semantic.bg.surfaceSubtle};
`;

export const MenuField = styled.div`
  width: 100%;

  & button[aria-haspopup='listbox'] {
    height: 72px;
    padding: ${space['space-400']} ${space['space-500']};
    border: ${size.stroke['stroke-default']} solid ${semantic.border.secondary};
    border-radius: ${size.radius['radius-md']};
    background: ${semantic.bg.canvas};
    font-size: ${fonts.size.base};
  }
`;

export const DepositBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${space['space-300']};
  padding: ${space['space-300']} ${space['space-400']};
  border: ${size.stroke['stroke-default']} solid ${semantic.border.info};
  border-radius: ${size.radius['radius-sm']};
  background: ${semantic.bg.infoSubtle};
  color: ${semantic.text.info};
  font-family: ${bodyFont};
  font-size: ${fonts.size.sm};
  line-height: ${primitives.typography.lineHeight.normal};
`;

export const DepositText = styled.span`
  flex: 1 0 0;
  min-width: 0;
`;

export const DepositDismiss = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 0 0 ${space['space-300']};
  border: none;
  border-left: ${size.stroke['stroke-default']} solid ${semantic.border.info};
  background: none;
  color: inherit;
  cursor: pointer;

  &:focus-visible {
    outline: ${size.stroke['stroke-thick']} solid ${semantic.border.selected};
    outline-offset: ${space['space-050']};
  }
`;

export const BottomBar = styled.div`
  position: sticky;
  bottom: var(--sticky-footer-offset-bottom, 0px);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space['space-400']};
  width: 100%;
  box-sizing: border-box;
  padding: ${space['space-300']} ${space['space-500']};
  background: ${semantic.bg.canvas};
  border-top: ${size.stroke['stroke-default']} solid ${semantic.border.default};

  @media (min-width: ${layout.desktopBreakpoint}) {
    padding: ${space['space-600']} ${space['space-1000']};
  }
`;

export const BottomBarTitle = styled.span`
  font-family: ${bodyFont};
  font-size: ${fonts.size.base};
  line-height: ${primitives.typography.lineHeight.normal};
  color: ${semantic.text.primary};
`;
